// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA CAMPANITA — DONDE VIVE UN AVISO CUANDO YA NO INTERRUMPE
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL AGUJERO QUE TAPA. El único sitio que leía los avisos internos era el modal de entrada
//  (AvisoAdminBanner.jsx), y ese modal descarta a propósito todo lo que pase de 24 horas para no
//  soltar seis ventanas seguidas. Bien pensado… salvo que no había NINGÚN otro sitio donde mirar:
//  un aviso que salía un sábado a las 08:00 ya no se pintaba en ninguna parte el lunes. Sin ver,
//  sin poder darse por leído, e invisible para siempre. Y no era cosa de un aviso concreto: le
//  pasaba a todo lo que manda `avisarEquipo` — el certificado que caduca, la jornada sin cerrar,
//  el trabajo de laboratorio, la urgencia del fin de semana.
//
//  POR QUÉ AQUÍ Y NO EN UNA PANTALLA NUEVA:
//   · Media docena de comentarios y de guardias de este repositorio ya hablaban de "la campanita"
//     como de algo que existía. Estaba escrita en todas partes menos en el código; en el panel de
//     administración incluso había ya el botón de campana pintado, sin `onClick` y sin contador.
//   · La página /notificaciones es de rol admin y solo SIRVE para enviar. El doctor al que le
//     caduca el certificado no entra ahí, así que no puede ser su bandeja.
//   · La cabecera es la única esquina que ve TODO el mundo, en cualquier pantalla y también en el
//     modo doctor a pantalla completa, que es justo donde está el doctor cuando le avisan.
//
//  EL REPARTO DE PAPELES QUEDA ASÍ: el modal INTERRUMPE con lo de las últimas 24 h (una vez, todo
//  junto); la campanita GUARDA los 90 días y deja darlos por leídos cuando a uno le venga bien.
//
//  B-161 · A SIMPLE VISTA, SOLO LO QUE NO HAS LEÍDO. El dueño: «los avisos en la campanita deberían
//  ocultarse en un desplegable que esté plegado para no molestar y que solo sean visibles a simple vista
//  los que no han sido leídos; para "leerlo" hay que pulsar un tick verde, y que salga un badge rojo con
//  el nº de notificaciones». Así que: arriba, los sin leer, cada uno con su TICK VERDE; lo leído va
//  plegado debajo, en «Leídos (N)»; desplegar un aviso para leerlo YA NO lo marca (echarle un vistazo no
//  es darlo por leído), lo marca el tick —o «Abrir y atender», que ya es atenderlo—. El número rojo de
//  la campana ya estaba.
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Ico } from './ui'
// t247 · la regla de la casa para cerrar un desplegable (pulsar o tocar fuera, Escape, el foco y atrás/adelante): un utils/ sin
// imports, que las guardias cargan de verdad.
import { engancharCierre } from '../utils/nubeDesplegables'
// t319 · con el panel de la ayuda abierto, el aviso fijo de una mención se pliega en una pastilla junto al globo (utils/panelAyuda).
import { estadoPanelAyuda, suscribirPanelAyuda, sitioDeLosAvisos, PASTILLA_AVISO } from '../utils/panelAyuda'
import { t as i18nT, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)
// B-868 · el tamaño del perfil es un zoom en <html> (utils/uiScale): lo medido en la pantalla (ratón, cajas, ventana)
// va en px de pantalla y lo que se escribe en un estilo se pinta multiplicado por el zoom; aquí se pasa a px de estilo.
// Sin import a propósito: las guardias montan este fichero con sus dobles.
const zoomDeLaPagina = () => { try { return parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { return 1 } }

export default function CampanaAvisos({ size = 32, radio = '50%' }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [abierto, setAbierto] = useState(false)
  const [abierta, setAbierta] = useState(null)     // id del aviso desplegado
  const caja = useRef(null)
  const boton = useRef(null)
  const panel = useRef(null)

  // ════════════════════════════════════════════════════════════════════════════════════════════
  //  B-684 · EL DESPLEGABLE SALIA CORTADO
  // ════════════════════════════════════════════════════════════════════════════════════════════
  //  EL DUEÑO, 18-sep-2026: «las notificaciones de la campana del header salen cortadas, no esta
  //  responsive».
  //
  //  POR QUE. El panel colgaba de la campana con `position: absolute`, asi que lo recortaba el
  //  primer antepasado con `overflow: hidden` — y lo hay: la cabecera de la aplicacion vive dentro
  //  de `#main-content` (components/Layout.jsx), que lo lleva para que el contenido no empuje la
  //  pagina. Todo lo que el panel se saliera del area de contenido (por la izquierda, por debajo de
  //  ella, o por detras del menu lateral) desaparecia. Y el `maxWidth: calc(100vw - 24px)` no
  //  salvaba nada: 100vw es la pantalla ENTERA, no el hueco donde el panel puede pintarse, asi que
  //  con el menu lateral abierto y una pantalla mediana el limite era mas ancho que el sitio real.
  //
  //  COMO SE ARREGLA. El panel pasa a `position: fixed` con las coordenadas MEDIDAS del boton: un
  //  elemento fijo se posiciona respecto a la ventana, asi que ningun `overflow` de un antepasado
  //  lo recorta. Y al medir se acota contra la ventana de verdad por los cuatro lados, de modo que
  //  ni se sale por la derecha, ni por la izquierda, ni por abajo (la lista se queda con el alto
  //  que haya y hace scroll ella). Se vuelve a medir al cambiar el tamaño de la ventana y al hacer
  //  scroll: si no, al girar el movil el panel se quedaria clavado donde estaba el boton antes.
  const [sitio, setSitio] = useState(null)
  // B-161 · lo leído va plegado: «Leídos (N)» lo despliega.
  const [verLeidos, setVerLeidos] = useState(false)
  const medir = () => {
    const b = boton.current && boton.current.getBoundingClientRect ? boton.current.getBoundingClientRect() : null
    if (!b || typeof window === 'undefined') return
    const z = zoomDeLaPagina()
    const vw = (window.innerWidth || 0) / z, vh = (window.innerHeight || 0) / z
    const ancho = Math.min(380, Math.max(220, vw - 16))
    // Anclado por la DERECHA al boton (como estaba), pero sin salirse por ninguno de los dos lados.
    let der = Math.max(8, vw - b.right / z)
    if (der + ancho > vw - 8) der = Math.max(8, vw - ancho - 8)
    const arriba = b.bottom / z + 9
    // Lo que quede de pantalla por debajo del boton, con un suelo: en un movil apaisado caben pocos
    // avisos, pero un panel de 120 px que hace scroll es utilizable y uno cortado no.
    const alto = Math.max(160, vh - arriba - 12)
    setSitio({ arriba, der, ancho, alto })
  }
  // Se mide ANTES de abrir (no en un efecto) para que el panel salga ya en su sitio: medido
  // despues, el primer pintado lo pondria en el sitio de antes y se veria saltar.
  const alternar = () => { if (!abierto) medir(); setAbierto(v => !v) }

  useEffect(() => {
    if (!abierto || typeof window === 'undefined' || !window.addEventListener) return undefined
    const otra = () => medir()
    window.addEventListener('resize', otra)
    window.addEventListener('scroll', otra, true)   // `true`: tambien el scroll de un contenedor
    return () => { window.removeEventListener('resize', otra); window.removeEventListener('scroll', otra, true) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto])

  const { data, isLoading } = useQuery({
    queryKey: ['avisos-mios'],
    queryFn: () => api.get('/avisos/mios').then(r => r.data).catch(() => null),
    enabled: !!user,
    refetchInterval: 90000,
  })
  const avisos = data?.avisos || []
  const sinLeer = Number(data?.sin_leer || 0)
  const dias = Number(data?.dias || 90)

  // ══════════════════════════════════════════════════════════════════════════════════════════════
  //  B-160 · SI TE MENCIONAN, EL AVISO NO SE VA HASTA QUE LO LEES
  // ══════════════════════════════════════════════════════════════════════════════════════════════
  //  EL DUEÑO: «si te citan con @ en un mensaje, que esa notificación sea tipo popup y no
  //  desaparezca hasta que accedes y lo lees». Lo que había: el aviso flotante del chat
  //  (ChatNotifFlotante) se cierra solo a los 6,5 s y es igual para una mención que para cualquier
  //  otro mensaje, y la línea de la mención en esta campana solo se ve si se abre la campana.
  //
  //  Así que la mención sin leer se queda FIJA en pantalla, debajo de la cabecera, en cualquier
  //  pantalla de la aplicación y del panel de administración (la campana está en las dos cabeceras,
  //  y hay una sola por pantalla). No lleva aspa a propósito: lo único que la quita es leer el
  //  mensaje, que es lo pedido. El dato es el de la línea de la campana (el servidor mira
  //  `chat_lecturas.ultimo_leido >= mensaje`), así que no hay un segundo «visto» que pueda llevarle
  //  la contraria al chat. Dentro del chat no sale (ahí ya se está leyendo) y con la campana abierta
  //  tampoco (la línea ya está en la lista).
  //
  //  QUE SALGA Y SE VAYA A TIEMPO. La lista de la campana se pide cada 90 s: el aviso tardaría minuto
  //  y medio en salir y otro tanto en irse después de leer el mensaje. Por eso escucha el número del
  //  chat (la misma consulta que el botón «Chat equipo», que ya se pide cada 20 s y que el chat
  //  refresca en cuanto marca algo como leído): si cambia, la lista se vuelve a pedir en el acto. Y
  //  al salir del chat, también.
  const { data: chatBadge } = useQuery({
    queryKey: ['chat-badge'],
    queryFn: () => api.get('/chat/no-leidos').then(r => r.data).catch(() => ({ total: 0 })),
    enabled: !!user,
    refetchInterval: 20000,
  })
  const ruta = (typeof window !== 'undefined' && window.location && window.location.pathname) || ''
  // `/chat` en la aplicación y `/administracion/chat` en el panel.
  const enChat = /(^|\/)chat(\/|$)/.test(ruta)
  const mencionesSinLeer = avisos.filter(a => a.mencion && !a.visto)

  // Cerrar al pulsar fuera o con Escape: es un desplegable de cabecera, no un modal que secuestre
  // la pantalla. Los oyentes solo existen mientras está abierto.
  // t247 (15-oct-2026) · CON LA REGLA DE LA CASA (utils/nubeDesplegables · engancharCierre), la misma que Mi perfil y «Opciones»
  // de la agenda. Antes escuchaba solo `mousedown` y en la fase de burbuja: en un iPhone tocar una zona sin botón no lo manda
  // (se quedaba abierta), y lo que paraba el clic por el camino (los menús de la agenda) también la dejaba abierta. Ahora, en
  // captura, con el ratón y con el dedo, y también se cierra cuando el foco se va o se va atrás.
  useEffect(() => {
    if (!abierto) return
    // B-684 · el panel YA NO cuelga de `caja` (es fijo, para que no lo recorte nadie), asi que hay
    // que preguntarle tambien a el: si no, pulsar DENTRO del propio desplegable lo cerraria.
    const dentro = (t) => {
      const enCaja = caja.current && caja.current.contains && caja.current.contains(t)
      const enPanel = panel.current && panel.current.contains && panel.current.contains(t)
      return !!(enCaja || enPanel)
    }
    return engancharCierre({ dentro, cerrar: () => setAbierto(false) })
  }, [abierto])

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ['avisos-mios'] })
    qc.invalidateQueries({ queryKey: ['avisos-nuevos'] })
  }
  const marcar = async (id) => { await api.post(`/avisos/${id}/visto`).catch(() => {}); refrescar() }

  // B-160 · cuando cambia el número del chat (llega un mensaje o alguien lo lee), se vuelve a pedir
  // la lista: así la mención aparece al llegar y se va al leerla. La primera lectura solo fija el
  // punto de partida.
  const totalChat = chatBadge ? (Number(chatBadge.total) || 0) : null
  const totalAntes = useRef(null)
  useEffect(() => {
    if (totalChat === null) return
    if (totalAntes.current !== null && totalAntes.current !== totalChat) refrescar()
    totalAntes.current = totalChat
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalChat])
  // Y al SALIR del chat: lo que se ha leído allí quita el aviso.
  const estabaEnChat = useRef(enChat)
  useEffect(() => {
    if (estabaEnChat.current && !enChat) refrescar()
    estabaEnChat.current = enChat
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enChat])
  const mencion = (!abierto && !enChat && mencionesSinLeer.length) ? mencionesSinLeer[0] : null
  const masMenciones = mencionesSinLeer.length - 1
  // t319 · EL PANEL DE LA AYUDA, ABIERTO: el aviso iba debajo de la cabecera, justo donde el panel tiene la suya, y como
  // vive dentro de la barra de arriba (capa 30) el panel (2000) lo tapaba: en el móvil quedaba una tira cortada o nada.
  // Con el panel abierto se pliega en la pastilla de al lado del globo (fuera del panel); a pantalla completa no hay sitio
  // y no se pinta hasta que se reduce o se cierra. Sigue sin aspa y sin temporizador: lo único que lo quita es leer.
  const [panelAyuda, setPanelAyuda] = useState(estadoPanelAyuda)
  useEffect(() => suscribirPanelAyuda(setPanelAyuda), [])
  const sitioMencion = sitioDeLosAvisos(panelAyuda)
  const pastilla = sitioMencion === 'pastilla'
  // Su botón y su pie, UNA vez: en la tarjeta van en la fila de abajo; en la pastilla, el botón a la derecha.
  const abrirMencion = mencion && (
    <button type="button" data-abrir-mencion onClick={() => { if (mencion.url) navigate(mencion.url) }}
      style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
      {i18nT('components.CampanaAvisos.abrir', 'Abrir')}
    </button>
  )
  const pieMencion = mencion && (
    <span data-mencion-pie style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4,
      ...(pastilla ? { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}) }}>
      {masMenciones > 0
        ? i18nT('components.CampanaAvisos.y_sin_leer', 'Y {{masMenciones}} {{x}} sin leer', { masMenciones, x: masMenciones === 1 ? i18nT('components.CampanaAvisos.mencion_mas', 'mención más') : i18nT('components.CampanaAvisos.menciones_mas', 'menciones más') })
        : i18nT('components.CampanaAvisos.se_quita_al_leer_el', 'Se quita al leer el mensaje')}
    </span>
  )
  const marcarTodos = async () => { await api.post('/avisos/vistos', { dias }).catch(() => {}); refrescar() }

  // B-691 · QUITARSE UN AVISO DE LA CAMPANA. No borra el aviso: lo oculta PARA QUIEN PULSA. El mismo
  // aviso le llega a más gente —muchos van a toda la clínica— y borrarlo se lo quitaría a quien aún
  // no lo ha leído.
  //
  // `sePuedeOcultar` lo dice el SERVIDOR, no se adivina aquí: mientras la clínica no tenga aplicada
  // la migración 2028-06-22, los botones NO se pintan. Ofrecer un botón que al pulsarlo no hace nada
  // es peor que no tenerlo.
  const sePuedeOcultar = data?.se_puede_ocultar === true
  const quitar = async (id) => { await api.post(`/avisos/${id}/ocultar`).catch(() => {}); refrescar() }
  // Solo lo YA LEÍDO, a propósito: vaciar la campana de un golpe llevándose lo que no has abierto es
  // la forma más rápida de perder justo el aviso que importaba. Para quitar uno sin leer está el
  // botón de su línea, que es una decisión sobre ESE aviso.
  const limpiarVistos = async () => { await api.post('/avisos/ocultar-vistos', { dias }).catch(() => {}); refrescar() }
  // Las menciones del chat no son filas de avisos: se quitan al leer el mensaje, no desde aquí.
  const hayVistosPropios = avisos.some(a => !a.mencion && a.visto)

  // B-161 · DESPLEGAR UN AVISO ES LEERLO, NO DARLO POR LEÍDO. Antes se marcaba en ese momento; el dueño
  // pidió lo contrario: «para "leerlo" hay que pulsar un tick verde». Se despliega y se lee entero, y se
  // da por leído con su tick (`marcarLeido`) o con «Abrir y atender».
  //
  // B-207 · Y LAS MENCIONES DEL CHAT NO SE MARCAN NUNCA DESDE AQUÍ, que es la parte que pidió el dueño expresamente. Esa
  // línea NO tiene un "visto" propio: cuelga del mensaje (`chat_lecturas.ultimo_leido >= mensaje`,
  // el mismo dato del número rojo). Marcarla aquí sería inventarle un segundo estado que algún día
  // diría lo contrario que el chat. Así que pulsarla LLEVA AL MENSAJE, y desaparece sola en cuanto
  // el mensaje quede leído — se llegue por aquí, por el push o por el número rojo.
  // t270 · LAS SOLICITUDES DE DERECHOS SIN ATENDER (`a.pendiente`), igual que las menciones: no son filas de avisos, las
  // calcula el servidor (services/derechosPendientes) y no tienen «leído» propio. Pulsarlas lleva a ESA solicitud y se van
  // solas de aquí cuando se resuelve o se deniega. Por eso no llevan tick ni aspa, y «Marcar todo» no las cuenta.
  const abrirAviso = (a) => {
    if (a.mencion || a.pendiente) { setAbierto(false); if (a.url) navigate(a.url); return }
    setAbierta(x => (x === a.id ? null : a.id))
  }
  // El tick verde de su línea: pasa a «Leídos» y deja de contar en el número rojo.
  const marcarLeido = (a) => { if (!a.visto && !a.mencion && !a.pendiente) marcar(a.id) }
  // «Abrir y atender» ya es atenderlo: se da por leído y se va a donde lleva.
  const atender = (a) => { if (!a.visto && !a.mencion && !a.pendiente) marcar(a.id); setAbierto(false); navigate(a.url) }
  const marcables = sinLeer - avisos.filter(a => a.pendiente && !a.visto).length

  // B-161 · LAS DOS LISTAS: lo que no has leído, a la vista; lo leído, plegado.
  const noLeidos = avisos.filter(a => !a.visto)
  const leidos = avisos.filter(a => a.visto)

  if (!user) return null

  return (
    <div ref={caja} style={{ position: 'relative', flexShrink: 0 }}>
      <button ref={boton} onClick={alternar} title={sinLeer ? i18nT('components.CampanaAvisos.avisos_sin_leer', 'Avisos ({{sinLeer}} sin leer)', { sinLeer }) : i18nT('components.CampanaAvisos.avisos', 'Avisos')} aria-label={i18nT('components.CampanaAvisos.avisos', 'Avisos')}
        style={{ position: 'relative', width: `${size}px`, height: `${size}px`, borderRadius: radio, border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer', color: abierto ? 'var(--primary)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
        <Ico n="bell" s={Math.round(size * 0.5)} />
        {sinLeer > 0 && (
          <span style={{ position: 'absolute', top: '-5px', right: '-5px', minWidth: '17px', height: '17px', padding: '0 4px', borderRadius: '9px', background: 'var(--danger)', color: '#fff', fontSize: '10.5px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, border: '2px solid var(--surface)' }}>
            {sinLeer > 99 ? '99+' : sinLeer}
          </span>
        )}
      </button>

      {abierto && (
        // B-684 · FIJO Y MEDIDO: ningun `overflow: hidden` de un antepasado puede recortarlo, y las
        // cuatro medidas salen de la ventana de verdad (ver `medir`). `flexDirection: column` +
        // `maxHeight` reparten el alto disponible: la cabecera se queda quieta y la lista scrollea.
        // t302 · `data-desplegable-cabecera`: abierto, sube la barra de arriba a la capa de los desplegables (index.css, «LAS
        // CAPAS DE LA APLICACIÓN»), encima de los avisos que flotan; sin él, valía lo que la barra y le pasaban por encima.
        <div ref={panel} data-desplegable-cabecera="" style={{ position: 'fixed', top: (sitio ? sitio.arriba : 0) + 'px', right: (sitio ? sitio.der : 8) + 'px', width: (sitio ? sitio.ancho : 380) + 'px', maxHeight: (sitio ? sitio.alto : 460) + 'px', display: 'flex', flexDirection: 'column', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 18px 44px rgba(0,0,0,0.22)', zIndex: 2400, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 15px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <span style={{ fontSize: '14.5px', fontWeight: 800, color: 'var(--text-primary)' }}>{i18nT('components.CampanaAvisos.avisos', 'Avisos')}</span>
            {sinLeer > 0 && <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-muted)' }}>{tx('components.CampanaAvisos.sin_leer', '{{sinLeer}} sin leer', { sinLeer })}</span>}
            {/* Los dos botones van juntos a la derecha y ENVUELVEN si no caben: en un móvil de
                390 px la cabecera tiene poco sitio, y antes «Marcar todo» solo cabía porque estaba
                solo. */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {marcables > 0 && (
                <button onClick={marcarTodos} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 9px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-secondary)', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}>
                  <Ico n="check" s={13} />{i18nT('components.CampanaAvisos.marcar_todo', ' Marcar todo')}
                </button>
              )}
              {/* B-691 · vaciar de un golpe lo que ya está leído. Solo sale si hay algo que quitar. */}
              {sePuedeOcultar && hayVistosPropios && (
                <button onClick={limpiarVistos} data-limpiar-vistos title={i18nT('components.CampanaAvisos.quita_de_tu_campana_los', 'Quita de tu campana los avisos que ya has leído. No los borra para los demás.')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 9px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-secondary)', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}>
                  <Ico n="trash" s={13} />{i18nT('components.CampanaAvisos.limpiar_leidos', ' Limpiar leídos')}
                </button>
              )}
            </div>
          </div>

          {/* B-684 · el alto ya lo pone el panel (medido contra la ventana): aqui solo se reparte.
              `minHeight: 0` es lo que permite que un hijo de un flex encoja y scrollee de verdad;
              sin el, la lista empuja al pie fuera del panel y vuelve a verse cortado. */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {isLoading && <div style={{ padding: '22px 15px', fontSize: '13px', color: 'var(--text-muted)' }}>{i18nT('components.CampanaAvisos.cargando', 'Cargando…')}</div>}
            {!isLoading && !avisos.length && (
              <div style={{ padding: '26px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-secondary)' }}>{i18nT('components.CampanaAvisos.no_tienes_avisos', 'No tienes avisos')}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{tx('components.CampanaAvisos.aqui_quedan_guardados_los_avisos', 'Aquí quedan guardados los avisos del equipo de los últimos {{dias}} días.', { dias })}</div>
              </div>
            )}
            {/* B-161 · SIN LEER, A LA VISTA. Si todo está leído se dice, y lo leído espera plegado debajo. */}
            {!isLoading && !!avisos.length && !noLeidos.length && (
              <div data-sin-no-leidos style={{ padding: '16px 15px', fontSize: '12.5px', color: 'var(--text-muted)', textAlign: 'center' }}>
                {i18nT('components.CampanaAvisos.no_tienes_avisos_sin_leer', 'No tienes avisos sin leer.')}
              </div>
            )}
            {[['noLeidos', noLeidos], ['leidos', verLeidos ? leidos : []]].map(([grupo, lista]) => (
              <div key={grupo} data-grupo-avisos={grupo}>
                {grupo === 'leidos' && !!leidos.length && (
                  <button type="button" data-ver-leidos aria-expanded={verLeidos} onClick={() => setVerLeidos(v => !v)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 15px', border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 800, cursor: 'pointer', textAlign: 'left' }}>
                    <Ico n={verLeidos ? 'chevronD' : 'chevronR'} s={13} />
                    {tx('components.CampanaAvisos.leidos', 'Leídos ({{length}})', { length: leidos.length })}
                    <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>{verLeidos ? i18nT('components.CampanaAvisos.plegar', 'Plegar') : i18nT('components.CampanaAvisos.ver', 'Ver')}</span>
                  </button>
                )}
            {lista.map((a, i) => {
              // Cabecera de día: "Hoy" / "Ayer" / la fecha, y la calcula el SERVIDOR en la zona de
              // la clínica. Restar días aquí con new Date() sería la trampa de siempre.
              const cabecera = i === 0 || lista[i - 1].dia !== a.dia
              const desplegado = abierta === a.id
              return (
                <div key={a.id}>
                  {cabecera && (
                    <div style={{ padding: '8px 15px 4px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-muted)', background: 'var(--surface-2)' }}>{a.dia}</div>
                  )}
                  <div onClick={() => abrirAviso(a)} data-derechos-sin-atender={a.pendiente ? a.solicitud_id : undefined}
                    style={{ display: 'flex', gap: '10px', padding: '11px 15px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: a.visto ? 'transparent' : 'color-mix(in srgb, var(--primary) 6%, transparent)',
                      ...(a.pendiente ? { borderLeft: '3px solid ' + (a.urgente ? 'var(--danger)' : 'var(--warning)') } : {}) }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px', flexShrink: 0, background: a.visto ? 'var(--border)' : a.pendiente ? (a.urgente ? 'var(--danger)' : 'var(--warning)') : 'var(--primary)' }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: a.visto ? 600 : 800, color: 'var(--text-primary)', lineHeight: 1.35 }}>{a.titulo}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{a.autor ? i18nT('components.CampanaAvisos.de', 'De {{autor}} · ', { autor: a.autor }) : ''}{a.fecha}</div>
                      {a.cuerpo && (
                        <div style={{ fontSize: '12.5px', lineHeight: 1.5, color: 'var(--text-secondary)', marginTop: '5px', whiteSpace: 'pre-wrap',
                          ...(desplegado ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>{a.cuerpo}</div>
                      )}
                      {/* La mención dice EN VOZ ALTA por qué no se puede marcar aquí: se quita al
                          leer el mensaje. Sin esta línea, "Marcar todo" la dejaría puesta y
                          parecería estropeada. */}
                      {a.mencion && (
                        <div data-mencion-chat={a.mensaje_id} style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                          {i18nT('components.CampanaAvisos.ir_al_mensaje_se_quita', 'Ir al mensaje. Se quita de aquí en cuanto lo leas, entres por donde entres.')}
                        </div>
                      )}
                      {a.pendiente && (
                        <div data-derechos-pie={a.solicitud_id} style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                          {i18nT('components.CampanaAvisos.ir_a_la_solicitud_se', 'Ir a la solicitud. Se quita de aquí cuando la resuelvas o la deniegues.')}
                        </div>
                      )}
                      {desplegado && a.url && !a.mencion && (
                        <button onClick={e => { e.stopPropagation(); atender(a) }}
                          style={{ marginTop: '8px', padding: '6px 11px', borderRadius: '8px', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{i18nT('components.CampanaAvisos.abrir_y_atender', 'Abrir y atender')}</button>
                      )}
                    </div>
                    {/* B-161 · EL TICK VERDE: darlo por leído. Solo en lo que no has leído, y nunca en una
                        mención del chat (esa se quita sola al leer el mensaje). `stopPropagation`: la fila
                        entera despliega, y el tick no tiene que desplegar nada. */}
                    {!a.visto && !a.mencion && !a.pendiente && (
                      <button data-marcar-leido={a.id} aria-label={i18nT('components.CampanaAvisos.marcar_como_leido', 'Marcar como leído')} title={i18nT('components.CampanaAvisos.marcar_como_leido_pasa_a', 'Marcar como leído: pasa a Leídos')}
                        onClick={e => { e.stopPropagation(); marcarLeido(a) }}
                        style={{ flexShrink: 0, alignSelf: 'flex-start', width: '26px', height: '26px', borderRadius: '50%', border: '1px solid var(--success)', background: 'var(--success-bg)', color: 'var(--success)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>
                        <Ico n="check" s={14} />
                      </button>
                    )}
                    {/* B-691 · QUITAR ESTE AVISO DE MI CAMPANA. En las menciones del chat NO se pinta:
                        esas no son avisos, se quitan solas al leer el mensaje, y una aspa aquí
                        parecería que esconde un mensaje sin leerlo.
                        `stopPropagation` es imprescindible: la fila entera es un botón que despliega
                        y marca como visto, así que sin él pulsar la aspa primero marcaría. */}
                    {sePuedeOcultar && !a.mencion && !a.pendiente && (
                      <button data-quitar-aviso={a.id} aria-label={i18nT('components.CampanaAvisos.quitar_este_aviso', 'Quitar este aviso')} title={i18nT('components.CampanaAvisos.quitar_de_mi_campana_no', 'Quitar de mi campana. No lo borra para los demás.')}
                        onClick={e => { e.stopPropagation(); quitar(a.id) }}
                        style={{ flexShrink: 0, alignSelf: 'flex-start', width: '24px', height: '24px', borderRadius: '7px', border: '1px solid transparent', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>
                        <Ico n="x" s={13} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
              </div>
            ))}
          </div>

          {/* UNA LISTA QUE SE CORTA TIENE QUE DECIRLO. Si el servidor devolvió menos de los que hay,
              se dice cuántos faltan en vez de dejar creer que eso era todo. */}
          <div style={{ padding: '9px 15px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.45, flexShrink: 0 }}>
            {data?.truncado
              ? i18nT('components.CampanaAvisos.se_muestran_los_mas_recientes', 'Se muestran los {{mostrados}} más recientes de {{total}} avisos de los últimos {{dias}} días.', { mostrados: data.mostrados, total: data.total, dias })
              : i18nT('components.CampanaAvisos.se_guardan_los_avisos_de', 'Se guardan los avisos de los últimos {{dias}} días.', { dias })}
          </div>
        </div>
      )}

      {/* B-160 · LA MENCIÓN SIN LEER, FIJA EN PANTALLA (ver arriba). Fija, como el desplegable: ningún
          `overflow` de la cabecera la recorta. En el mismo sitio que el aviso flotante del chat, que
          sale ENCIMA mientras dura (2500): al irse, esta se queda. Y POR DEBAJO del chat flotante y
          de los modales (1990 < 2000), para no tapar justo lo que se abre para leerla. */}
      {/* t319 · CON EL PANEL DE LA AYUDA ABIERTO, PLEGADA EN LA PASTILLA de al lado del globo (utils/panelAyuda): el mismo
          aviso, con su único botón «Abrir» y sin aspa, pero de una línea y fuera del panel. A pantalla completa, no se pinta. */}
      {mencion && sitioMencion !== 'oculto' && (
        <div data-aviso-mencion={mencion.mensaje_id} data-aviso-pastilla={pastilla ? '' : undefined} role="status" aria-live="polite"
          style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 70px)', right: '16px', width: 'min(370px, calc((100vw / var(--z, 1)) - 32px))', zIndex: 1990,
            display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px', boxSizing: 'border-box',
            background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: '4px solid var(--primary)',
            borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
            ...(pastilla ? { ...PASTILLA_AVISO, alignItems: 'center', gap: '9px', padding: '6px 6px 6px 9px', borderRadius: '14px' } : {}) }}>
          <span aria-hidden="true" style={{ width: pastilla ? '30px' : '36px', height: pastilla ? '30px' : '36px', borderRadius: '50%', flexShrink: 0, background: 'var(--primary)', color: 'var(--sobre-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: pastilla ? '15px' : '18px', fontWeight: 800, lineHeight: 1 }}>@</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div title={pastilla ? mencion.titulo : undefined} style={pastilla
              ? { fontSize: '12.5px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
              : { fontSize: '13.5px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.35, overflowWrap: 'anywhere' }}>{mencion.titulo}</div>
            {!pastilla && mencion.cuerpo && (
              <div style={{ fontSize: '12.5px', lineHeight: 1.45, color: 'var(--text-secondary)', marginTop: '3px', overflowWrap: 'anywhere',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{mencion.cuerpo}</div>
            )}
            {pastilla ? pieMencion : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                {abrirMencion}
                {pieMencion}
              </div>
            )}
          </div>
          {pastilla && abrirMencion}
        </div>
      )}
    </div>
  )
}
